import { ReactNode } from 'react';

import {
  alpha,
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';

interface AnimatedCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  isSelected?: boolean;
  onClick: () => void;
}

const AnimatedCard = ({
  title,
  description,
  icon,
  color,
  bgColor,
  borderColor,
  isSelected = false,
  onClick,
}: AnimatedCardProps) => {
  const theme = useTheme();

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 4,
        border: isSelected
          ? `1.5px solid ${color}`
          : `1px solid ${borderColor}`,
        backgroundColor: isSelected ? alpha(color, 0.04) : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
          borderColor: color,
          backgroundColor: alpha(color, 0.02),
        },
        boxShadow: isSelected
          ? `0 4px 20px ${alpha(color, 0.12)}`
          : '0 2px 8px rgba(0,0,0,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: isSelected
            ? `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`
            : 'transparent',
          transition: 'all 0.3s ease-in-out',
        },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 3,
          flex: 1,
          pt: 4,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '16px',
            backgroundColor: isSelected
              ? alpha(color, 0.12)
              : alpha(color, 0.08),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
            transition: 'all 0.3s ease-in-out',
            '& svg': {
              fontSize: 24,
            },
            '&:hover': {
              transform: 'scale(1.05)',
              backgroundColor: alpha(color, 0.15),
            },
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1.5,
            color: theme.palette.text.primary,
            fontSize: '0.95rem',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            fontSize: '0.875rem',
            opacity: 0.8,
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AnimatedCard;
